import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listCustomers, listTenantsOptions, createCustomer, updateCustomer } from "@/lib/admin-config.functions";
import { listCustomerMargins, refreshCustomerProfitability } from "@/lib/customer-margin.functions";
import { listCloneableCustomers, cloneCustomerSettings } from "@/lib/customer-cloning.functions";
import { runServerFn } from "@/lib/server-fn";
import { useAuth, tenantIdsWithRole } from "@/hooks/use-auth";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Loader2, Pencil, Search, Settings, Truck, Plug, RefreshCw, Palette } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { CustomerConfigDialog } from "@/components/admin/customer-config-dialog";
import { PortalBrandingDialog } from "@/components/admin/portal-branding-dialog";

export const Route = createFileRoute("/_authenticated/app/admin/customers/")({ component: CustomersPage });

type Addr = { line1?: string; line2?: string; city?: string; region?: string; postal_code?: string; country?: string };
const EMPTY_ADDR: Addr = {};
const ADDR_KEYS = [
  { key: "ship_from", label: "Ship From" },
  { key: "return_to", label: "Return To" },
  { key: "business_physical", label: "Business Physical Address" },
] as const;
type AddrKey = (typeof ADDR_KEYS)[number]["key"];

function CustomersPage() {
  const qc = useQueryClient();
  const { roles } = useAuth();
  const { isSuperAdmin: su } = useScope();
  const adminTenantIds = tenantIdsWithRole(roles, "tenant_admin");

  const fetchTenants = useServerFn(listTenantsOptions);
  const fetchCustomers = useServerFn(listCustomers);
  const createFn = useServerFn(createCustomer);
  const updateFn = useServerFn(updateCustomer);
  const fetchMargins = useServerFn(listCustomerMargins);
  const refreshMargins = useServerFn(refreshCustomerProfitability);
  const fetchCloneable = useServerFn(listCloneableCustomers);
  const cloneFn = useServerFn(cloneCustomerSettings);
  const [cloneFromId, setCloneFromId] = useState<string>("");
  const [refreshing, setRefreshing] = useState(false);

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [tenantId, setTenantId] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [configCustomer, setConfigCustomer] = useState<any | null>(null);
  const [brandingCustomer, setBrandingCustomer] = useState<any | null>(null);
  const [addresses, setAddresses] = useState<Record<AddrKey, Addr>>({
    ship_from: {}, return_to: {}, business_physical: {},
  });

  const { data: tenants } = useQuery({
    queryKey: ["tenants-options"],
    queryFn: () => runServerFn(() => fetchTenants(), "Loading tenants"),
  });

  const allowedTenants = useMemo(() => {
    if (!tenants) return [];
    if (su) return tenants;
    return tenants.filter((t: any) => adminTenantIds.includes(t.id));
  }, [tenants, su, adminTenantIds]);

  const { data: customers, isLoading } = useQuery({
    queryKey: ["customers"],
    queryFn: () => runServerFn(() => fetchCustomers(), "Loading customers"),
  });

  const customerIds = useMemo(
    () => (customers ?? []).map((c: any) => c.id),
    [customers]
  );
  const { data: margins } = useQuery({
    queryKey: ["customer-margins", customerIds],
    enabled: customerIds.length > 0,
    queryFn: () => runServerFn(
      () => fetchMargins({ data: { customerIds, months: 3 } }),
      "Loading margins"
    ),
  });
  const marginByCustomer = useMemo(() => {
    const m = new Map<string, { margin_cents: number; margin_pct: number }>();
    (margins?.customers ?? []).forEach((c: any) =>
      m.set(c.customer_id, { margin_cents: c.margin_cents, margin_pct: c.margin_pct })
    );
    return m;
  }, [margins]);

  const onRefreshMargins = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const month = new Date();
      month.setUTCDate(1);
      const monthStr = month.toISOString().slice(0, 10);
      const tenantIds = Array.from(new Set((customers ?? []).map((c: any) => c.tenant_id)));
      await Promise.all(
        tenantIds.map((tid) =>
          runServerFn(
            () => refreshMargins({ data: { tenantId: tid as string, month: monthStr } }),
            "Refreshing margins"
          )
        )
      );
      toast.success("Margins refreshed");
      qc.invalidateQueries({ queryKey: ["customer-margins"] });
    } catch { /* toast handled */ }
    finally { setRefreshing(false); }
  };

  const tenantName = (id: string) => tenants?.find((t: any) => t.id === id)?.name ?? "—";

  const filtered = useMemo(() => {
    if (!customers) return [];
    const s = search.trim().toLowerCase();
    if (!s) return customers;
    return customers.filter((c: any) => c.name.toLowerCase().includes(s));
  }, [customers, search]);

  const openCreate = () => {
    setEditingId(null); setName(""); setEmail(""); setPhone(""); setTenantId("");
    setCloneFromId("");
    setAddresses({ ship_from: {}, return_to: {}, business_physical: {} });
    setOpen(true);
  };
  const openEdit = (c: { id: string; name: string; tenant_id: string; contact_info: unknown }) => {
    const ci = (c.contact_info ?? {}) as { email?: string; phone?: string; addresses?: Partial<Record<AddrKey, Addr>> };
    setEditingId(c.id); setName(c.name); setEmail(ci.email ?? ""); setPhone(ci.phone ?? ""); setTenantId(c.tenant_id);
    setCloneFromId("");
    setAddresses({
      ship_from: ci.addresses?.ship_from ?? {},
      return_to: ci.addresses?.return_to ?? {},
      business_physical: ci.addresses?.business_physical ?? {},
    });
    setOpen(true);
  };

  const setAddrField = (key: AddrKey, field: keyof Addr, value: string) =>
    setAddresses((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));

  const cleanAddr = (a: Addr): Addr | null => {
    const out: Addr = {};
    (Object.keys(a) as (keyof Addr)[]).forEach((k) => {
      const v = (a[k] ?? "").trim();
      if (v) out[k] = v;
    });
    return Object.keys(out).length ? out : null;
  };

  const { data: cloneSources } = useQuery({
    queryKey: ["cloneable-customers", tenantId],
    enabled: !editingId && !!tenantId,
    queryFn: () => runServerFn(() => fetchCloneable({ data: { tenantId } }), "Loading source customers"),
  });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    const contactInfo: { email?: string; phone?: string; addresses?: Record<string, Addr> } = {};
    if (email.trim()) contactInfo.email = email.trim();
    if (phone.trim()) contactInfo.phone = phone.trim();
    const addrOut: Record<string, Addr> = {};
    (Object.keys(addresses) as AddrKey[]).forEach((k) => {
      const c = cleanAddr(addresses[k]);
      if (c) addrOut[k] = c;
    });
    if (Object.keys(addrOut).length) contactInfo.addresses = addrOut;
    try {
      if (editingId) {
        await runServerFn(() => updateFn({ data: { id: editingId, name: name.trim(), contactInfo } }), "Updating customer");
      } else {
        if (!tenantId) { setSubmitting(false); return; }
        const created = await runServerFn(() => createFn({ data: { tenantId, name: name.trim(), contactInfo } }), "Creating customer");
        if (cloneFromId && created?.id) {
          await runServerFn(
            () => cloneFn({ data: {
              sourceCustomerId: cloneFromId,
              targetCustomerId: created.id,
              sections: ["billing_rules", "routing_guides", "commodity_defaults", "automation_rules"],
            } }),
            "Cloning settings from source"
          );
        }
      }
      toast.success(editingId ? "Customer updated" : "Customer created");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["customers"] });
    } catch { /* toast handled */ }
    finally { setSubmitting(false); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Customers</h2>
          <p className="text-sm text-muted-foreground">Brands you operate the warehouse for.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="pl-8 w-56" />
          </div>
          <Button variant="outline" size="sm" onClick={onRefreshMargins} disabled={refreshing || !customers?.length}>
            {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh margins
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} disabled={allowedTenants.length === 0}><Plus className="h-4 w-4 mr-2" />New customer</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingId ? "Edit customer" : "Create customer"}</DialogTitle></DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4">
              {!editingId && (<div className="space-y-2">
                <Label>Tenant</Label>
                <Select value={tenantId} onValueChange={setTenantId}>
                  <SelectTrigger><SelectValue placeholder="Select a tenant" /></SelectTrigger>
                  <SelectContent>
                    {allowedTenants.map((t: any) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>)}
              {!editingId && tenantId && (
                <div className="space-y-2">
                  <Label>Clone settings from <span className="text-muted-foreground">(optional)</span></Label>
                  <Select value={cloneFromId || "__none"} onValueChange={(v) => setCloneFromId(v === "__none" ? "" : v)}>
                    <SelectTrigger><SelectValue placeholder="Start blank" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Start blank</SelectItem>
                      {(cloneSources ?? []).filter((c: any) => c).map((c: any) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Copies billing rules, routing guides, commodity defaults, and automations.</p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="c-name">Name</Label>
                <Input id="c-name" required value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label htmlFor="c-email">Contact email</Label><Input id="c-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
                <div className="space-y-2"><Label htmlFor="c-phone">Contact phone</Label><Input id="c-phone" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
              </div>
              <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
                {ADDR_KEYS.map(({ key, label }) => {
                  const a = addresses[key];
                  return (
                    <div key={key} className="border rounded-md p-3 space-y-2">
                      <h4 className="text-sm font-medium">{label}</h4>
                      <Input placeholder="Street address" value={a.line1 ?? ""} onChange={(e) => setAddrField(key, "line1", e.target.value)} />
                      <Input placeholder="Suite / unit (optional)" value={a.line2 ?? ""} onChange={(e) => setAddrField(key, "line2", e.target.value)} />
                      <div className="grid grid-cols-3 gap-2">
                        <Input placeholder="City" value={a.city ?? ""} onChange={(e) => setAddrField(key, "city", e.target.value)} />
                        <Input placeholder="State / region" value={a.region ?? ""} onChange={(e) => setAddrField(key, "region", e.target.value)} />
                        <Input placeholder="Postal code" value={a.postal_code ?? ""} onChange={(e) => setAddrField(key, "postal_code", e.target.value)} />
                      </div>
                      <Input placeholder="Country (2-letter, e.g. US)" maxLength={2} value={a.country ?? ""} onChange={(e) => setAddrField(key, "country", e.target.value.toUpperCase())} />
                    </div>
                  );
                })}
              </div>
              <DialogFooter>
                <Button type="submit" disabled={submitting || (!editingId && !tenantId)}>
                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editingId ? "Save" : "Create"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="border rounded-lg bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead><TableHead>Tenant</TableHead><TableHead>Contact</TableHead><TableHead>Margin (90d)</TableHead><TableHead>Created</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : filtered.length > 0 ? (
              filtered.map((c: any) => {
                const ci = (c.contact_info ?? {}) as { email?: string; phone?: string };
                const m = marginByCustomer.get(c.id);
                const pct = m?.margin_pct ?? null;
                const cents = m?.margin_cents ?? null;
                const tone =
                  pct == null ? "text-muted-foreground" :
                  pct < 0 ? "text-destructive" :
                  pct < 10 ? "text-amber-500" :
                  "text-emerald-500";
                return (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="text-muted-foreground">{tenantName(c.tenant_id)}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{[ci.email, ci.phone].filter(Boolean).join(" · ") || "—"}</TableCell>
                  <TableCell className={`text-sm tabular-nums ${tone}`}>
                    {cents == null ? "—" : `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })} (${pct!.toFixed(1)}%)`}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <Button asChild size="sm" variant="ghost" title="Carriers">
                      <Link to="/app/admin/customers/$customerId/carriers" params={{ customerId: c.id }}>
                        <Truck className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button asChild size="sm" variant="ghost" title="Integrations">
                      <Link to="/app/admin/customers/$customerId/integrations" params={{ customerId: c.id }}>
                        <Plug className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfigCustomer(c)} title="Configure"><Settings className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setBrandingCustomer(c)} title="Portal branding"><Palette className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(c)} title="Edit"><Pencil className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              );})
            ) : (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No customers yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <CustomerConfigDialog
        customer={configCustomer}
        onClose={() => setConfigCustomer(null)}
        onSaved={() => qc.invalidateQueries({ queryKey: ["customers"] })}
      />
      <PortalBrandingDialog
        customerId={brandingCustomer?.id ?? null}
        customerName={brandingCustomer?.name ?? null}
        open={!!brandingCustomer}
        onClose={() => setBrandingCustomer(null)}
      />
    </div>
  );
}
